import { blue, green, grey, red } from '@mui/material/colors';
import type { PaletteColor, PaletteColorOptions } from '@mui/material/styles';
import { alpha, createTheme } from '@mui/material/styles';

// A custom theme for this app
let newTheme = createTheme({
  typography: {
    fontFamily: [
      'Pretendard Variable',
      'Pretendard',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),

    H1_Looko: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },

    H2_Looko: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },

    Title_Looko: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },

    Subtitle_Looko: {
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },

    Body_Looko: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.2,
    },

    Button_Looko: {
      fontSize: '0.8125rem',
      fontWeight: 400,
      lineHeight: 1.2,
    },

    Label_Looko: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.2,
    },
  },
});

newTheme = createTheme(newTheme, {
  palette: {
    primary: newTheme.palette.augmentColor({
      color: {
        main: blue[500],
        dark: '#1E88E5',
      },
      name: 'primary',
    }),

    neutral: newTheme.palette.augmentColor({
      color: {
        main: grey[300],
        contrastText: '#000',
      },
      name: 'primary',
    }),

    'text.secondary': newTheme.palette.augmentColor({
      color: {
        main: 'rgba(0, 0, 0, 0.6)',
      },
      name: 'text.secondary',
    }),

    // Chips
    'chip.secondary': newTheme.palette.augmentColor({
      color: {
        main: '#E4F2FD',
        dark: alpha('#E4F2FD', 0.7),
        contrastText: '#2196F3',
      },
      name: 'chip.secondary',
    }),
    'chip.tertiary': newTheme.palette.augmentColor({
      color: {
        main: grey[200],
        contrastText: '#000',
      },
      name: 'chip.tertiary',
    }),
    'chip.white': newTheme.palette.augmentColor({
      color: {
        main: '#fff',
        dark: grey[200],
        contrastText: '#2196F3',
      },
      name: 'chip.white',
    }),
    'chip.red': newTheme.palette.augmentColor({
      color: {
        main: red[50],
        dark: alpha('#EF5350', 0.25),
        contrastText: '#EF5350',
      },
      name: 'chip.red',
    }),
    'chip.green': newTheme.palette.augmentColor({
      color: {
        main: green[50],
        dark: alpha(green[700], 0.25),
        contrastText: green[700],
      },
      name: 'chip.green',
    }),
    'chip.purple': newTheme.palette.augmentColor({
      color: {
        main: '#EDE7F6',
        dark: alpha('#7B1FA2', 0.25),
        contrastText: '#7B1FA2',
      },
      name: 'chip.purple',
    }),
    'chip.gray': newTheme.palette.augmentColor({
      color: {
        main: grey[100],
        dark: grey[200],
        contrastText: grey[500],
      },
      name: 'chip.gray',
    }),
    'chip.outlined.primary': newTheme.palette.augmentColor({
      color: {
        main: '#2196F3',
        dark: alpha('#2196F3', 0.1),
        contrastText: '#2196F3',
      },
      name: 'chip.outlined.primary',
    }),
    'chip.outlined.secondary': newTheme.palette.augmentColor({
      color: {
        main: grey[400],
        dark: alpha(grey[700], 0.2),
        contrastText: newTheme.palette.text.secondary,
      },
      name: 'chip.outlined.secondary',
    }),
  },
});

declare module '@mui/material/styles' {
  interface Palette {
    neutral: PaletteColor;
    'text.secondary': PaletteColor;

    'chip.secondary': PaletteColor;
    'chip.tertiary': PaletteColor;
    'chip.white': PaletteColor;
    'chip.red': PaletteColor;
    'chip.green': PaletteColor;
    'chip.purple': PaletteColor;
    'chip.gray': PaletteColor;
    'chip.outlined.primary': PaletteColor;
    'chip.outlined.secondary': PaletteColor;
  }

  // allow configuration using `createTheme`
  interface PaletteOptions {
    neutral?: PaletteColorOptions;
    'text.secondary': PaletteColorOptions;

    'chip.secondary': PaletteColorOptions;
    'chip.tertiary': PaletteColorOptions;
    'chip.white': PaletteColorOptions;
    'chip.red': PaletteColorOptions;
    'chip.green': PaletteColorOptions;
    'chip.purple': PaletteColorOptions;
    'chip.gray': PaletteColorOptions;
    'chip.outlined.primary': PaletteColorOptions;
    'chip.outlined.secondary': PaletteColorOptions;
  }

  interface TypographyVariants {
    H1_Looko: React.CSSProperties;
    H2_Looko: React.CSSProperties;
    Title_Looko: React.CSSProperties;
    Subtitle_Looko: React.CSSProperties;
    Body_Looko: React.CSSProperties;
    Button_Looko: React.CSSProperties;
    Label_Looko: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    H1_Looko?: React.CSSProperties;
    H2_Looko?: React.CSSProperties;
    Title_Looko?: React.CSSProperties;
    Subtitle_Looko?: React.CSSProperties;
    Body_Looko?: React.CSSProperties;
    Button_Looko?: React.CSSProperties;
    Label_Looko?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    H1_Looko: true;
    H2_Looko: true;
    Title_Looko: true;
    Subtitle_Looko: true;
    Body_Looko: true;
    Button_Looko: true;
    Label_Looko: true;
  }
}

// @babel-ignore-comment-in-output Update the Button's color prop options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
    'text.secondary': true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    'chip.secondary': true;
    'chip.tertiary': true;
    'chip.white': true;
    'chip.red': true;
    'chip.green': true;
    'chip.purple': true;
    'chip.gray': true;
    'chip.outlined.primary': true;
    'chip.outlined.secondary': true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    neutral: true;
    'text.secondary': true;
  }
}

declare module '@mui/material' {
  interface ListItemComponentsPropsOverrides {
    'data-testid': string;
  }
}

/**
 * Looko 커스텀 MUI 테마.
 *
 * 커스텀 Typography 변형:
 * - H1_Looko, H2_Looko: 제목 (1.5rem/1.25rem, weight 600/700)
 * - Title_Looko, Subtitle_Looko: 소제목 (1rem/0.875rem, weight 700)
 * - Body_Looko: 본문 (0.875rem, weight 400)
 * - Button_Looko: 버튼 텍스트 (0.8125rem, weight 400)
 * - Label_Looko: 레이블 (0.75rem, weight 400)
 *
 * 커스텀 Chip 색상: chip.secondary | chip.tertiary | chip.white |
 *   chip.red | chip.green | chip.purple | chip.gray |
 *   chip.outlined.primary | chip.outlined.secondary
 */
export const theme = newTheme;
